import React from 'react';
import {
  ListResponse,
  PaymentMethodType,
  Purchaser,
} from '@ordermentum/ui-types';
import { ToastReduxAction } from '@ordermentum/ui-definitions';
import classNames from 'classnames';
import { KeyedMutator } from 'swr';
import Switch from '../../Input/Switch';
import Banner from '../../Banner';
import { removeDefaultPayment, updateDefaultPayment } from '../helpers';
import C from '../../../constants';

type StateProps = {
  type: PaymentMethodType;
  paymentMethodId?: string;
  purchasers?: Purchaser[];
  allowToggleAll?: boolean;
  formikFunction?: (...args) => void;
  formikValue?: boolean;
  showToastAction?: (toast: ToastReduxAction) => Promise<void>;
  purchasersMutate?: KeyedMutator<ListResponse<Purchaser>>;
};

export const PurchaserToggleBlockComponent = (props: StateProps) => {
  const {
    purchasers,
    type,
    paymentMethodId,
    allowToggleAll = false,
    formikValue,
    formikFunction = () => {},
    showToastAction = () => {},
    purchasersMutate,
  } = props;

  let filteredPurchasers = purchasers?.filter(purchaser =>
    purchaser?.paymentMethodTypes?.value?.includes(type)
  );
  if (!filteredPurchasers) filteredPurchasers = [];

  const onSetDefaultPayment = async (purchaserId, checked) => {
    if (checked) {
      try {
        await updateDefaultPayment({
          paymentMethodId,
          purchaserId,
        });
        showToastAction({
          notificationType: 'success',
          type: C.NOTIFY_TOGGLE_DEFAULT_PAYMENT_FULFILLED,
          notificationPayload: `Updated default payment`,
          notificationDelay: C.NOTIFICATION_DELAY,
        });
      } catch (ex) {
        showToastAction({
          notificationType: 'error',
          type: C.NOTIFY_TOGGLE_DEFAULT_PAYMENT_REJECTED,
          notificationPayload: `Failed to update default payment`,
          notificationDelay: C.NOTIFICATION_DELAY,
        });
      }
    } else {
      try {
        await removeDefaultPayment({
          paymentMethodId: null,
          purchaserId,
          ignorePaymentMethodId: paymentMethodId,
        });
        showToastAction({
          notificationType: 'success',
          type: C.NOTIFY_DELETE_PAYMENT_METHOD_FAILURE,
          notificationPayload: `Removed default payment`,
          notificationDelay: C.NOTIFICATION_DELAY,
        });
      } catch (ex) {
        showToastAction({
          notificationType: 'error',
          type: C.NOTIFY_DELETE_PAYMENT_METHOD_SUCCESS,
          notificationPayload: `Failed to remove default payment`,
          notificationDelay: C.NOTIFICATION_DELAY,
        });
      }
    }

    if (purchasersMutate) {
      await purchasersMutate();
    }
  };

  return (
    <div
      className="flex flex-col gap-xs"
      data-testid="payment-methods-purchaser-toggle-block"
    >
      {!allowToggleAll && filteredPurchasers.length > 0 && (
        <label className="m-0 font-body text-label-small text-subtle">
          Use this payment method for:
        </label>
      )}
      <div
        className={classNames(
          'flex flex-col',
          !allowToggleAll &&
            filteredPurchasers.length > 0 &&
            'border-b-[0.063rem] border-solid border-foreground-subtle'
        )}
      >
        {allowToggleAll && (
          <div className="flex">
            <p className="flex grow font-body text-base font-bold">
              Use as default payment method
            </p>
            <Switch
              name="default-for-all"
              checked={formikValue}
              onClick={(_, value) => {
                formikFunction(value);
              }}
            />
          </div>
        )}
        {!allowToggleAll &&
          filteredPurchasers.length > 0 &&
          filteredPurchasers.map(purchaser => (
            <div
              className="border-t border-solid border-foreground-subtle px-0 pb-s pt-xs"
              key={`${purchaser.id}${paymentMethodId}`}
            >
              <div className="flex flex-row gap-xs">
                <div
                  style={{
                    backgroundImage: `url(${
                      purchaser?.supplier?.logo?.logo || ''
                    })`,
                  }}
                  className="size-8 rounded-xxxs border border-solid border-foreground-subtle bg-contain bg-no-repeat"
                />
                <div className="flex grow items-center">
                  <label className="m-0 font-body text-label text-strong">
                    {purchaser?.supplier?.name}
                  </label>
                </div>
                <div className="flex items-center">
                  <Switch
                    name={purchaser?.supplier?.name}
                    checked={purchaser.paymentMethodId === paymentMethodId}
                    onClick={(e, value) => {
                      onSetDefaultPayment(purchaser.id, value);
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
      </div>
      {!allowToggleAll && filteredPurchasers.length === 0 && (
        <Banner variant="info">
          You are not connected with any suppliers who accept this payment
          method. <br />
          To use this method, ask your supplier to enable it for you.
        </Banner>
      )}
    </div>
  );
};

export default PurchaserToggleBlockComponent;
